import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/cn";

/**
 * PO-54 option B : marque du prix de carte (card.price / price_average.average_price),
 * vérifiée côté serveur sur le prix stocké lui-même (src/estimationFilters.js cardPriceBasis).
 *  - raw             : le prix affiché est le calcul brut pondéré (sql/card_price.sql :
 *                      historique et relevés courants, ventes non gradées) ;
 *  - history_only    : calcul brut, aucun relevé courant ⇒ historique seul ;
 *  - current_only    : calcul brut, aucune vente datée (réglage strict) ⇒ courant seul ;
 *  - stale           : ventes brutes présentes, mais le prix affiché n'en est pas le calcul ;
 *  - fallback_no_raw : aucune vente brute, ancien prix conservé (repli) ;
 *  - null            : aucun prix (ou prix ≤ 0) ⇒ rien affiché.
 * Composant unique, partagé par la fiche, le carrousel et la recherche (pas de copie par écran).
 * `compact` (listes) : seules les marques qui disent « ce n'est pas un prix brut mesuré »
 * sont affichées ; « brut » n'est affiché qu'en vue détail.
 */
export type PriceBasis = "raw" | "history_only" | "current_only" | "stale" | "fallback_no_raw" | null;

export function PriceBasisBadge({
  basis,
  compact = false,
  className,
}: {
  basis?: PriceBasis | string | null;
  compact?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const tr = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  let mark: { tone: "neutral" | "warn"; text: string; help: string } | null = null;
  if (basis === "fallback_no_raw") {
    mark = {
      tone: "warn",
      text: tr("card.price_basis.fallback", "fallback, no raw sale"),
      help: tr("card.price_basis.fallback_help", "No raw sale retained: previous card price kept. Not a measured raw price."),
    };
  } else if (basis === "stale") {
    mark = {
      tone: "warn",
      text: tr("card.price_basis.stale", "to recompute"),
      help: tr("card.price_basis.stale_help", "Raw sales exist, but the displayed price is not computed from them. Not a measured raw price."),
    };
  } else if (basis === "raw" && !compact) {
    mark = {
      tone: "neutral",
      text: tr("card.price_basis.raw", "raw"),
      help: tr("card.price_basis.raw_help", "Measured on raw (ungraded) sales only: history and current listings, weighted."),
    };
  } else if (basis === "history_only" && !compact) {
    mark = {
      tone: "neutral",
      text: tr("card.price_basis.history_only", "history only"),
      help: tr("card.price_basis.history_only_help", "Measured on raw sales only, no current listing: computed on history alone."),
    };
  } else if (basis === "current_only" && !compact) {
    mark = {
      tone: "neutral",
      text: tr("card.price_basis.current_only", "current only"),
      help: tr("card.price_basis.current_only_help", "Measured on raw sales only, no dated sale: computed on current listings alone."),
    };
  }
  if (!mark) return null;
  return (
    <Badge
      tone={mark.tone}
      variant="soft"
      title={mark.help}
      data-testid="price-basis"
      data-basis={basis || ""}
      className={cn(compact ? "text-[9px]" : undefined, className)}
    >
      {mark.text}
    </Badge>
  );
}
